import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Download, ExternalLink, AlertTriangle, AlertCircle, CheckCircle2, ArrowUpRight, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import CreditsStatement from '../../components/subscription/CreditsStatement';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// ───────────────────────── helpers ─────────────────────────

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
};

const formatNumber = (n) => Number(n || 0).toLocaleString('de-DE');

const STATUS_LABELS = {
  active:          { label: 'Aktiv',                  color: 'bg-green-100 text-green-700' },
  pending_payment: { label: 'Zahlung ausstehend',     color: 'bg-yellow-100 text-yellow-700' },
  payment_failed:  { label: 'Zahlung fehlgeschlagen', color: 'bg-red-100 text-red-700' },
  cancelling:      { label: 'Wird gekündigt',         color: 'bg-orange-100 text-orange-700' },
  cancelled:       { label: 'Gekündigt',              color: 'bg-gray-100 text-gray-500' },
  free:            { label: 'Kein Plan',              color: 'bg-gray-100 text-gray-500' },
};

// Map raw backend status → one of 4 display states
const mapDisplayState = (raw) => {
  if (raw === 'active') return 'ACTIVE';
  if (raw === 'pending_payment') return 'PENDING';
  if (raw === 'payment_failed' || raw === 'cancelling') return 'PROBLEM';
  return 'FREE';
};

const INVOICE_STATUS_BADGE = {
  paid:          { label: 'Bezahlt',   color: 'bg-green-100 text-green-700' },
  open:          { label: 'Offen',     color: 'bg-gray-100 text-gray-600' },
  draft:         { label: 'Entwurf',   color: 'bg-gray-100 text-gray-600' },
  void:          { label: 'Storniert', color: 'bg-gray-100 text-gray-500' },
  uncollectible: { label: 'Ausfall',   color: 'bg-red-100 text-red-700' },
};

const StatusBadge = ({ status }) => {
  const b = STATUS_LABELS[status] || STATUS_LABELS.free;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${b.color}`}
          data-testid={`billing-status-badge-${status}`}>
      {b.label}
    </span>
  );
};

const InvoiceStatusBadge = ({ status }) => {
  const b = INVOICE_STATUS_BADGE[status] || { label: status || '—', color: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${b.color}`}>
      {b.label}
    </span>
  );
};

// ───────────────────── feature-list builder ─────────────────────

const buildFeatureList = (plan) => {
  if (!plan) return [];
  const limits = plan.limits || {};
  const features = plan.features || {};
  const list = [
    `${formatNumber(limits.credits_monthly)} Credits / Monat`,
    `${limits.projects ?? 0} Projekt${(limits.projects ?? 0) === 1 ? '' : 'e'}`,
    `${limits.connected_channels_social ?? 0} Social Channel${(limits.connected_channels_social ?? 0) === 1 ? '' : 's'}`,
    `${limits.connected_channels_website ?? 0} Webseite${(limits.connected_channels_website ?? 0) === 1 ? '' : 'n'}`,
  ];
  if (features.video_generation) list.push('Videogenerierung');
  if (features.press_release_distribution) list.push('Pressemitteilung-Verteilung');
  return list;
};

// ───────────────────────── component ─────────────────────────

export default function BillingTab({ getAuthHeader }) {
  const [loading, setLoading] = useState(true);
  const [planData, setPlanData] = useState(null);
  const [allPlans, setAllPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [actioning, setActioning] = useState(null);
  const navigate = useNavigate();
  const configure = () => navigate('/app/upgrade');   // Paket wird im Konfigurator gewählt und per E-Mail angefragt

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [myRes, allRes, invRes] = await Promise.all([
        axios.get(`${API}/plans/my-plan`, { headers: getAuthHeader() }),
        axios.get(`${API}/plans/all`).catch(() => ({ data: [] })),
        axios.get(`${API}/billing/invoices`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
      ]);
      setPlanData(myRes.data || null);
      setAllPlans(Array.isArray(allRes.data) ? allRes.data : []);
      setInvoices(Array.isArray(invRes.data) ? invRes.data : []);
    } catch (e) {
      console.error('Billing load failed', e);
      toast.error('Abrechnungsdaten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    if (params.get('billing') === 'success') {
      toast.success('Abonnement erfolgreich aktiviert 🎉');
      const url = new URL(window.location.href);
      url.searchParams.delete('billing');
      window.history.replaceState({}, '', url.toString());
    }
  }, [load]);

  const goPortal = async () => {
    setActioning('portal');
    try {
      const r = await axios.post(`${API}/billing/portal`, {}, { headers: getAuthHeader() });
      if (r.data?.portal_url) window.location.href = r.data.portal_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Stripe-Portal konnte nicht geöffnet werden');
    } finally { setActioning(null); }
  };

  const goCheckout = async (planName) => {
    setActioning(`checkout-${planName}`);
    try {
      const r = await axios.post(`${API}/billing/create-checkout`, { plan: planName.toLowerCase() }, { headers: getAuthHeader() });
      if (r.data?.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout konnte nicht gestartet werden');
    } finally { setActioning(null); }
  };

  // ── derived data ───────────────────────────────────────────────────────
  const rawStatus = planData?.subscription_status || 'free';
  const display = mapDisplayState(rawStatus);
  const plan = planData?.plan;
  const limits = planData?.plan_limits || plan?.limits || {};
  const features = planData?.plan_features || plan?.features || {};
  const usage = planData?.usage || { credits_used: 0, videos_used: 0, videos_addon_used: 0 };
  const planName = planData?.plan_name || plan?.name || 'Kein Plan';

  const creditsUsed = Number(usage.credits_used || 0);
  const creditsTotal = Number(limits.credits_monthly || 0);
  const creditsPct = creditsTotal > 0 ? Math.min(100, Math.round((creditsUsed / creditsTotal) * 100)) : 0;

  const videosUsed = Number(usage.videos_used || 0);
  const videosTotal = Number(limits.videos_monthly || 0);
  const videosPct = videosTotal > 0 ? Math.min(100, Math.round((videosUsed / videosTotal) * 100)) : 0;

  const nextResetDate = planData?.next_reset_date || usage?.reset_date;
  const paymentDeadline = planData?.payment_deadline;
  const invoiceUrl = planData?.stripe_pending_invoice_url;
  const renewalDate = planData?.subscription_renewal_date;

  const higherPlans = useMemo(() => {
    if (!plan || !plan.price_euros) return allPlans.filter(p => Number(p.price_euros) > 0);
    return allPlans.filter(p => Number(p.price_euros) > Number(plan.price_euros));
  }, [allPlans, plan]);

  if (loading) {
    return <div className="flex justify-center py-12" data-testid="billing-loading"><Loader2 className="w-6 h-6 animate-spin text-[#1e9ead]" /></div>;
  }

  // ── render by display state ────────────────────────────────────────────
  return (
    <div className="space-y-6" data-testid="billing-tab" data-state={display}>
      {display === 'FREE' && <FreeState onConfigure={configure} />}

      {display === 'PENDING' && (
        <PendingState
          planName={planName} limits={limits} features={features} usage={usage}
          creditsUsed={creditsUsed} creditsTotal={creditsTotal} creditsPct={creditsPct}
          videosUsed={videosUsed} videosTotal={videosTotal} videosPct={videosPct}
          nextResetDate={nextResetDate}
          paymentDeadline={paymentDeadline} invoiceUrl={invoiceUrl}
        />
      )}

      {display === 'PROBLEM' && (
        <ProblemState
          rawStatus={rawStatus} renewalDate={renewalDate}
          onPortal={goPortal} actioning={actioning}
          planName={planName} limits={limits} features={features}
          creditsUsed={creditsUsed} creditsTotal={creditsTotal} creditsPct={creditsPct}
          videosUsed={videosUsed} videosTotal={videosTotal} videosPct={videosPct}
          nextResetDate={nextResetDate}
        />
      )}

      {display === 'ACTIVE' && (
        <ActiveState
          planName={planName} plan={plan} limits={limits} features={features} usage={usage}
          creditsUsed={creditsUsed} creditsTotal={creditsTotal} creditsPct={creditsPct}
          videosUsed={videosUsed} videosTotal={videosTotal} videosPct={videosPct}
          nextResetDate={nextResetDate}
          onConfigure={configure} onPortal={goPortal}
          actioning={actioning} rawStatus={rawStatus}
        />
      )}

      {/* Credits: Guthaben, Verbrauch, Verlauf (immer sichtbar, auch in der Testphase) */}
      <CreditsStatement getAuthHeader={getAuthHeader} />

      {/* Invoices table — always rendered if there are any */}
      {invoices.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="billing-invoices">
          <div className="px-6 py-4 border-b border-gray-200">
            <h4 className="text-base font-semibold text-gray-900">Rechnungen</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-3 text-left font-medium">Datum</th>
                  <th className="px-6 py-3 text-left font-medium">Rechnungsnr.</th>
                  <th className="px-6 py-3 text-left font-medium">Betrag</th>
                  <th className="px-6 py-3 text-left font-medium">Status</th>
                  <th className="px-6 py-3 text-left font-medium">PDF</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {invoices.map(inv => (
                  <tr key={inv.id} className="hover:bg-gray-50" data-testid={`invoice-row-${inv.id}`}>
                    <td className="px-6 py-3 text-gray-900">{inv.date}</td>
                    <td className="px-6 py-3 text-gray-600 font-mono text-xs">{inv.number || '—'}</td>
                    <td className="px-6 py-3 text-gray-900 font-medium">
                      {Number(inv.amount).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {inv.currency}
                    </td>
                    <td className="px-6 py-3"><InvoiceStatusBadge status={inv.status} /></td>
                    <td className="px-6 py-3">
                      {inv.pdf_url ? (
                        <a href={inv.pdf_url} target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-center gap-1 text-[#1e9ead] hover:text-[#197b87] font-medium"
                           data-testid={`invoice-download-${inv.id}`}>
                          <Download className="w-3.5 h-3.5" /> Herunterladen
                        </a>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────── Sub-components ──────────────────────────

const FreeState = ({ onConfigure }) => (
  <div data-testid="billing-state-free">
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 md:p-8 text-center max-w-3xl mx-auto">
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Finde dein Paket</h2>
      <p className="text-sm text-gray-500 mb-5">Wähle deine Kanäle und wie oft du sie bespielen willst. Wir berechnen die Credits und zeigen dir das passende Paket.</p>
      <Button onClick={onConfigure} className="bg-[#1e9ead] hover:bg-[#188796] text-white" data-testid="billing-configure-btn">Paket konfigurieren</Button>
    </div>
  </div>
);

const PendingState = ({ planName, features, paymentDeadline, invoiceUrl, creditsUsed, creditsTotal, creditsPct, videosUsed, videosTotal, videosPct, nextResetDate, limits }) => (
  <div data-testid="billing-state-pending">
    {/* Orange banner */}
    <div className="rounded-xl p-4 md:p-5 mb-6"
         style={{ background: '#FEF3C7', border: '1px solid #F59E0B', color: '#92400E' }}
         data-testid="pending-banner">
      <div className="flex items-start gap-3 flex-wrap">
        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-[200px]">
          <p className="font-medium text-sm md:text-base mb-3">
            Du hast eine offene Rechnung. Bitte zahle bis <strong>{formatDate(paymentDeadline)}</strong> um deinen Zugang nicht zu verlieren.
          </p>
          {invoiceUrl ? (
            <a href={invoiceUrl} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-[#92400E] text-white hover:bg-[#78350F] transition-colors"
               data-testid="pending-pay-btn">
              Rechnung öffnen &amp; bezahlen
              <ArrowUpRight className="w-4 h-4" />
            </a>
          ) : (
            <p className="text-sm">Bitte kontaktiere uns unter <a href="mailto:support@xchannels.ai" className="underline font-medium">support@xchannels.ai</a></p>
          )}
        </div>
      </div>
    </div>

    {/* Plan info card */}
    <PlanInfoCard
      planName={planName} subtitle="Dein Plan ist aktiv bis zur Zahlungsdeadline"
      badge={<StatusBadge status="pending_payment" />}
      features={features} limits={limits}
      creditsUsed={creditsUsed} creditsTotal={creditsTotal} creditsPct={creditsPct}
      videosUsed={videosUsed} videosTotal={videosTotal} videosPct={videosPct}
      nextResetDate={nextResetDate}
    />
  </div>
);

const ProblemState = ({ rawStatus, renewalDate, onPortal, actioning, planName, features, limits, creditsUsed, creditsTotal, creditsPct, videosUsed, videosTotal, videosPct, nextResetDate }) => {
  const message = rawStatus === 'cancelling'
    ? `Dein Abo wurde gekündigt und läuft am ${formatDate(renewalDate)} aus.`
    : 'Deine Zahlung ist fehlgeschlagen. Bitte aktualisiere deine Zahlungsmethode um Unterbrechungen zu vermeiden.';
  return (
    <div data-testid="billing-state-problem">
      <div className="rounded-xl p-4 md:p-5 mb-6"
           style={{ background: '#FEE2E2', border: '1px solid #EF4444', color: '#991B1B' }}
           data-testid="problem-banner">
        <div className="flex items-start gap-3 flex-wrap">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-[200px]">
            <p className="font-medium text-sm md:text-base mb-3">{message}</p>
            <button onClick={onPortal} disabled={actioning === 'portal'}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-[#991B1B] text-white hover:bg-[#7F1D1D] transition-colors disabled:opacity-50"
                    data-testid="problem-portal-btn">
              {actioning === 'portal' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
              Zahlungsmethode aktualisieren
            </button>
          </div>
        </div>
      </div>

      <PlanInfoCard
        planName={planName} badge={<StatusBadge status={rawStatus} />}
        features={features} limits={limits}
        creditsUsed={creditsUsed} creditsTotal={creditsTotal} creditsPct={creditsPct}
        videosUsed={videosUsed} videosTotal={videosTotal} videosPct={videosPct}
        nextResetDate={nextResetDate}
      />
    </div>
  );
};

const ActiveState = ({ planName, plan, features, limits, creditsUsed, creditsTotal, creditsPct, videosUsed, videosTotal, videosPct, nextResetDate, onConfigure, onPortal, actioning }) => (
  <div data-testid="billing-state-active">
    <PlanInfoCard
      planName={planName} badge={<StatusBadge status="active" />}
      features={features} limits={limits}
      creditsUsed={creditsUsed} creditsTotal={creditsTotal} creditsPct={creditsPct}
      videosUsed={videosUsed} videosTotal={videosTotal} videosPct={videosPct}
      nextResetDate={nextResetDate}
      action={
        <Button onClick={onPortal} disabled={actioning === 'portal'} variant="outline"
                className="border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
                data-testid="billing-portal-btn">
          {actioning === 'portal' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
          Zahlungsmittel verwalten
        </Button>
      }
    />

    <div className="mt-6" data-testid="billing-upgrade-section">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">Paket ändern</h4>
          <p className="text-sm text-gray-500">Konfiguriere deine Kanäle neu und sieh, welches Paket zu dir passt.</p>
        </div>
        <Button onClick={onConfigure} variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50 text-sm" data-testid="billing-change-plan-btn">Paket konfigurieren</Button>
      </div>
    </div>
  </div>
);

const PlanInfoCard = ({ planName, badge, subtitle, action, features, limits, creditsUsed, creditsTotal, creditsPct, videosUsed, videosTotal, videosPct, nextResetDate }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 md:p-6"
       data-testid="billing-plan-info-card">
    <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900" data-testid="billing-plan-name">{planName}</h3>
          {badge}
        </div>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>
      {action}
    </div>

    {/* Credits progress */}
    <div className="mt-5">
      <div className="flex items-center justify-between text-sm mb-2">
        <span className="text-gray-600">Credits</span>
        <span className="text-gray-900 font-medium" data-testid="billing-credits-text">
          {formatNumber(creditsUsed)} / {formatNumber(creditsTotal)} <span className="text-gray-400 ml-1">({creditsPct}%)</span>
        </span>
      </div>
      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full bg-[#1e9ead] rounded-full transition-all"
             style={{ width: `${creditsPct}%` }} data-testid="billing-credits-bar" />
      </div>
      {nextResetDate && (
        <p className="text-xs text-gray-500 mt-2" data-testid="billing-reset-date">
          Nächster Credit-Reset: <span className="text-gray-700 font-medium">{formatDate(nextResetDate)}</span>
        </p>
      )}
    </div>

    {features.video_generation && (
      <div className="mt-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-gray-600">Videos</span>
          <span className="text-gray-900 font-medium" data-testid="billing-videos-text">
            {videosUsed} / {videosTotal}
          </span>
        </div>
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full bg-[#1D9E75] rounded-full transition-all"
               style={{ width: `${videosPct}%` }} data-testid="billing-videos-bar" />
        </div>
      </div>
    )}

    <div className="mt-6 pt-5 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <h4 className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">Features</h4>
        <div className="flex flex-wrap gap-2">
          {features.video_generation && <FeatureBadge label="Videogenerierung" />}
          {features.press_release_distribution && <FeatureBadge label="Pressemitteilung-Verteilung" />}
          {features.priority_support && <FeatureBadge label="Priority Support" />}
          {!features.video_generation && !features.press_release_distribution && !features.priority_support && (
            <span className="text-xs text-gray-400">Keine Zusatzfeatures</span>
          )}
        </div>
      </div>
      <div>
        <h4 className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">Limits</h4>
        <div className="grid grid-cols-3 gap-3 text-sm">
          <StatBox value={limits.projects ?? '—'} label="Projekte" testId="limit-projects" />
          <StatBox value={limits.connected_channels_social ?? '—'} label="Social" testId="limit-channels-social" />
          <StatBox value={limits.connected_channels_website ?? '—'} label="Webseiten" testId="limit-channels-web" />
        </div>
      </div>
    </div>
  </div>
);

const FeatureBadge = ({ label }) => (
  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#1e9ead]/10 text-[#197b87] border border-[#1e9ead]/30">
    <CheckCircle2 className="w-3 h-3 mr-1" />
    {label}
  </span>
);

const StatBox = ({ value, label, testId }) => (
  <div data-testid={testId}>
    <div className="text-xl font-semibold text-gray-900">{value}</div>
    <div className="text-xs text-gray-500">{label}</div>
  </div>
);

const PlanCard = ({ plan, cta, onClick, busy, testId }) => {
  const list = buildFeatureList(plan);
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 md:p-6 flex flex-col"
         data-testid={testId}>
      <h4 className="text-lg font-semibold text-gray-900">{plan.name}</h4>
      <div className="my-3">
        <span className="text-3xl font-bold text-[#1e9ead]">€{formatNumber(plan.price_euros)}</span>
        <span className="text-sm text-gray-500 ml-1">/ Monat</span>
      </div>
      <ul className="space-y-2 mb-6 flex-1 text-sm text-gray-700">
        {list.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check className="w-4 h-4 text-[#1e9ead] mt-0.5 flex-shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Button onClick={onClick} disabled={busy}
              className="bg-[#1e9ead] hover:bg-[#197b87] text-white w-full"
              data-testid={`${testId}-cta`}>
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
        {cta}
      </Button>
    </div>
  );
};
